/**
 * Set view component.
 * @module components/View/SetView
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Confirm,
  Table,
  TableRow,
  TableHeader,
  TableCell,
  TableHeaderCell,
} from 'semantic-ui-react';
import { Link, useHistory } from 'react-router-dom';

import { Icon } from '@plone/volto/components';
import { deleteContent, getContent } from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';

import leftKeySVG from '@plone/volto/icons/left-key.svg';
import deleteSVG from '@plone/volto/icons/delete.svg';
import pencilSVG from '@plone/volto/icons/pencil.svg';

// EUR has no live exchange rate of its own yet, so it's shown as a flat
// $5 markup over the USD price.
const formatPrice = (price, currency) => {
  if (!price) return 'N/A';
  return currency === 'EUR' ? `€ ${Number(price) + 5}` : `$ ${price}`;
};
const formatInventory = (inventory) => inventory ?? 0;

const fullcode = (content) =>
  content.code_variant
    ? `${content.code}.${content.code_variant}`
    : content.code;

const fullsize = (content) =>
  content.size_variant
    ? `${content.size}${content.size_variant}`
    : content.size;

/**
 * Set view component class.
 * @function SetView
 * @param {Object} content Content object.
 * @returns {string} Markup of the component.
 */
const SetView = ({ content }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const currency = useSelector((state) => state.currency);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const parentUrl = flattenToAppURL(content['@id'])
    .split('/')
    .slice(0, -1)
    .join('/');

  const parent = useSelector(
    (state) => state.content.subrequests?.parent?.data,
  );

  useEffect(() => {
    dispatch(getContent(parentUrl, null, 'parent'));
  }, [dispatch, parentUrl]);

  const onDelete = async () => {
    await dispatch(deleteContent(flattenToAppURL(content['@id'])));
    setShowDeleteConfirm(false);
    history.push(parentUrl);
  };

  return (
    <>
      <div className="button-toolbar">
        <button
          type="button"
          className="circular-button float-left"
          onClick={() => history.push(parentUrl)}
          aria-label="Back"
        >
          <Icon name={leftKeySVG} size="24px" />
        </button>
        <Link
          className="circular-button float-left"
          to={`${flattenToAppURL(content['@id'])}/edit`}
        >
          <Icon name={pencilSVG} size="24px" />
        </Link>
        <button
          type="button"
          className="circular-button float-left"
          onClick={() => setShowDeleteConfirm(true)}
        >
          <Icon name={deleteSVG} size="24px" />
        </button>
        <div className="button-toolbar-title">
          {parent?.category ? parent?.category : ''}
        </div>
      </div>
      <Confirm
        open={showDeleteConfirm}
        header="Delete set"
        content={`Are you sure you want to delete "${parent?.title} - Set ${fullsize(content)}"?`}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={onDelete}
      />
      <div className="view-padding">
        <article id="set">
          <h1 className="content-title">
            {parent?.title}
            {' - Set '}
            {fullcode(content)}
            {fullsize(content)}
          </h1>
          <Table celled>
            <TableHeader>
              <TableRow>
                <TableHeaderCell
                  rowSpan="5"
                  textAlign="center"
                  className="general sub rotated"
                >
                  <span className="rotate-label">General</span>
                </TableHeaderCell>
                <TableHeaderCell width="6">Size</TableHeaderCell>
                <TableCell width="6">
                  {fullcode(content)}
                  {fullsize(content)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableHeaderCell>Sold separate</TableHeaderCell>
                <TableCell>{content.sold_separate ? 'Yes' : 'No'}</TableCell>
              </TableRow>
              <TableRow>
                <TableHeaderCell>Price</TableHeaderCell>
                <TableCell>{formatPrice(content.price, currency)}</TableCell>
              </TableRow>
              <TableRow>
                <TableHeaderCell>Inventory</TableHeaderCell>
                <TableCell>{formatInventory(content.inventory)}</TableCell>
              </TableRow>
              <TableRow>
                <TableHeaderCell>Tools</TableHeaderCell>
                <TableCell>
                  {content.set ? content.set.split('\n').join(', ') : ''}
                </TableCell>
              </TableRow>
            </TableHeader>
          </Table>
        </article>
      </div>
    </>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
SetView.propTypes = {
  content: PropTypes.shape({
    code: PropTypes.string,
    name: PropTypes.string,
    category: PropTypes.string,
  }).isRequired,
};

export default SetView;
